/**
 * Base Template - Used for International Cities (LATAM, OTHER regions)
 * This preserves the current clean design for all non-US markets
 */

import { BRAND_PERSON_NAME, BRAND_FAN_URL } from "@/lib/brandConfig";
import { buildTallyContactEmbedDataSrc } from "@/lib/tallyContactEmbed";
import MuxVideoPlayer from "@/components/MuxVideoPlayer";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "react-i18next";
// Amplitude tracking removed to prevent React error #310
import {
  trackTemplateUsageGA,
  trackWhatsAppBroadcastClickGA,
} from "@/lib/analytics";
import {
  applyInstagramBrowserCSS,
  getInstagramOptimizations,
} from "@/lib/instagramBrowserDetection";
import {
  getBaseTemplateSessionId,
  trackBaseTemplateClick,
} from "@/lib/baseTemplateAnalytics";
import { useBaseTemplatePassiveSessionSummary } from "@/hooks/useBaseTemplatePassiveSessionSummary";
import {
  trackImageOpen,
  trackImageClose,
} from "@/lib/imageViewTracking";
import {
  trackLightboxClosedPostHog,
  trackLightboxNavigationPostHog,
  trackLightboxOpenedPostHog,
  trackMediaItemClickPostHog,
  trackTemplateUsagePostHog,
  trackWhatsAppBroadcastPostHog,
} from "@/lib/posthogAnalytics";
import { ChevronLeft, ChevronRight, Play, Sparkles, X, Film } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import type { VideoActionButton } from "@/components/MuxVideoPlayer";
import { trackWhatsAppBroadcastMeta } from "@/lib/metaPixelAnalytics";
import { getBaseTemplateMuxPlaybackIds } from "@/lib/muxApi";
import { useImageProtection } from "@/hooks/useImageProtection";
import {
  getCloudinaryUrl,
  isSameCloudinaryImage,
  resolveLogoUrl,
} from "@/lib/cloudinaryUtils";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BaseTemplateFooter } from "@/components/templates/BaseTemplateFooter";
import "@/utils/muxCacheUtils"; // Expose cache utils to window for console access
import { resolveField, useResolvedCtas } from "@/hooks/useResolvedCtas";
import { ensureTallyEmbeds } from "@/lib/loadAnalytics";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";

// Type for Cloudflare Worker injected template config
interface TemplateConfig {
  country?: string;
  province?: string;
  city?: string;
  templateName?: string;
  language?: string;
  hideCta?: boolean;
  showWishlist?: boolean;
  showCommunity?: boolean;
  showBirthday?: boolean;
  showDualVideo?: boolean;
  whatsappBroadcast?: boolean;
  detectionMethod?: string;
}

declare global {
  interface Window {
    __TEMPLATE_CONFIG__?: TemplateConfig;
    Tally?: { loadEmbeds: () => void };
  }
}

// Manual fallback: playback IDs with external_id "feed" and creator_id = BRAND_MUX_CREATOR_ID in Mux.
// Used when Mux API fails or returns no results. Do not remove.
const MANUAL_FALLBACK_PLAYBACK_IDS: string[] = [
  "55af92c0a6afac58e780413127c73c5c",
  "27cbea077c11d9cc0569774b8c443a04",
  "0599f80309c4103f7ebe835cf09f5a41",
  "DyyJ3idqFVxOW9Mtj9MUUhRpS19WqdWTmxeJyvntULw",
  "ANe9AEYt1jHyuBLSN1SVDoTah727CCoNMffDvUc005RM",
];

// Resolve fallback: prefer VITE_MUX_FALLBACK_PLAYBACK_IDS (comma-separated) when set;
// otherwise use manual fallback above. Only runs when BaseTemplate is mounted (not on /sorpresa, /exclusive, etc.).
function getFallbackPlaybackIds(): string[] {
  const envPlaybackIds = import.meta.env.VITE_MUX_FALLBACK_PLAYBACK_IDS;

  if (envPlaybackIds && typeof envPlaybackIds === "string") {
    const ids = envPlaybackIds
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (ids.length > 0) {
      if (import.meta.env.DEV) {
        console.log(
          `[BaseTemplate] ${ids.length} playback IDs desde variable de entorno`,
        );
      }
      return ids;
    }
  }

  if (import.meta.env.DEV) {
    console.log("[BaseTemplate] IDs de respaldo manuales (feed external_id)");
  }
  return [...MANUAL_FALLBACK_PLAYBACK_IDS];
}

export interface TemplateProps {
  mediaItems: Array<{
    id: number;
    type: "video" | "photo";
    src: string;
    title: string;
    playbackId?: string; // Mux playback ID (required for videos in BaseTemplate)
    actionButton?: VideoActionButton; // Optional: URL de destino para el botón del video
    // NO cloudflareId, NO embedId - BaseTemplate only uses Mux videos
  }>;
  // Optional: disable Mux API filtering when using pre-validated media from database
  skipMuxApiFilter?: boolean;
  // Optional: URL for header logo/name link (managed from admin)
  headerUrl?: string;
  // Optional: Logo image URL (Cloudinary, Cloudflare, etc.) - managed from admin
  logoUrl?: string;
  // Optional: Hero title and description (from admin site-settings)
  heroTitle?: string;
  heroDescription?: string;
  // Optional: Banner/hero background image (URL o public_id). Si no se pasa, usa Cloudflare por defecto.
  bannerImageUrl?: string;
  /** Cuando true, no mostrar img del banner hasta tener la URL final (evita flash Cloudflare → site settings) */
  bannerDeferred?: boolean;
  /** Cuando true, no mostrar galería hasta tener datos de site settings (evita carga escalonada) */
  galleryDeferred?: boolean;
  /** Botón CTA extra en el hero (debajo del broadcast de WhatsApp). Solo se muestra si se pasa. */
  heroCta?: React.ReactNode;
  /** false = ocultar enlace /portfolio en el nav (sin portfolio público). Por defecto true. */
  showPortfolioNavLink?: boolean;
  brandName?: string;
  footerTagline?: string;
  /** Id del formulario Tally de contacto (site_settings.tally_contact_form_id).
   *  Si no se pasa, se usa TALLY_CONTACT_FORM_ID por defecto. */
  tallyContactFormId?: string;
  /** Admin global "Hero: ocultar todos los CTA" (site_settings.hide_global_hero_cta),
   *  leído client-side desde /api/site-settings. Se OR-ea con el flag del Worker
   *  (`templateConfig.hideCta`) para que aplique al instante aunque la propagación
   *  KV del Worker no haya llegado (sin Upstash/Postgres KV el cache es por instancia). */
  hideHeroCta?: boolean;
}

const WHATSAPP_BROADCAST_URL = "/whatsapp-broadcast";

function sanitizeMenuWishlistUrl(raw: string, fallback: string): string {
  const t = raw.trim();
  if (!t) return fallback;
  if (!t.startsWith("http://") && !t.startsWith("https://")) return fallback;
  try {
    const u = new URL(t);
    if (u.protocol !== "http:" && u.protocol !== "https:") return fallback;
    return u.toString();
  } catch {
    return fallback;
  }
}

function sanitizeBroadcastHref(raw: string): string {
  const t = raw.trim();
  if (!t) return WHATSAPP_BROADCAST_URL;
  if (t.startsWith("/")) return t;
  if (t.startsWith("http://") || t.startsWith("https://")) return t;
  return WHATSAPP_BROADCAST_URL;
}

export const BaseTemplate: React.FC<TemplateProps> = ({
  mediaItems,
  skipMuxApiFilter = false,
  headerUrl = "/",
  logoUrl,
  heroTitle,
  heroDescription,
  bannerImageUrl,
  bannerDeferred = false,
  galleryDeferred = false,
  heroCta,
  showPortfolioNavLink = true,
  brandName = BRAND_PERSON_NAME,
  footerTagline,
  tallyContactFormId,
  hideHeroCta = false,
}) => {
  // headerUrl = destino del clic. Si parece public_id Cloudinary, usar /
  const normalizedHeaderUrl =
    headerUrl?.startsWith("/") || headerUrl?.startsWith("http") ? headerUrl : "/";
  const { language } = useLanguage();
  const { t } = useTranslation();
  const location = useLocation();
  const templateConfig = getServerTemplateConfig();
  const countryCode = templateConfig?.country;
  useBaseTemplatePassiveSessionSummary({
    templateName: "BaseTemplate",
    countryCode,
    language,
    pathname: location.pathname,
    galleryKey: mediaItems.length,
  });
  useImageProtection();
  const showWhatsAppBroadcast = !!templateConfig?.whatsappBroadcast;

  const { ctas } = useResolvedCtas();
  const wishlistFallback = `${BRAND_FAN_URL}/r/wishlist`;
  const wishlistMenuUrl = sanitizeMenuWishlistUrl(
    resolveField(ctas.vip_membership, "menu_wishlist_url", wishlistFallback),
    wishlistFallback,
  );
  const whatsappBroadcastHref = sanitizeBroadcastHref(
    resolveField(ctas.telegram_basic, "broadcast_href", WHATSAPP_BROADCAST_URL),
  );
  // Fallback del embed de Tally: id editable desde admin (site_settings) → default.
  // El CTA override de admin (special_offer.tally_embed_src) sigue teniendo prioridad.
  const tallyFallbackSrc = buildTallyContactEmbedDataSrc(tallyContactFormId);
  const tallyEmbedSrc = resolveField(
    ctas.special_offer,
    "tally_embed_src",
    tallyFallbackSrc,
    (raw) => {
      const u = raw.trim();
      if (!u) return tallyFallbackSrc;
      const withScheme =
        u.startsWith("https://") ? u : u.startsWith("http://") ? u : `https://${u}`;
      try {
        const parsed = new URL(withScheme);
        if (!parsed.hostname.endsWith("tally.so")) return tallyFallbackSrc;
        return parsed.toString();
      } catch {
        return tallyFallbackSrc;
      }
    },
  );

  // ── CTA interno: leer flags del Worker (ya están inyectados) ──
  // OR del flag admin (site_settings, client-side) con el del Worker: si CUALQUIERA
  // pide ocultar, se ocultan todos los CTA del hero. Así el toggle del admin aplica
  // al instante aunque el Worker/KV todavía no haya propagado.
  const hideCta = !!templateConfig?.hideCta || hideHeroCta;
  const showCommunity = !!templateConfig?.showCommunity;
  const showBirthday = !!templateConfig?.showBirthday;
  const showDualVideo = !!templateConfig?.showDualVideo;
  const showWishlist = !!templateConfig?.showWishlist;


  // ── Server-side render tracking → /admin/live-funnel + Slack #anaafernandez ──
  // Records WHICH CTA variant the worker resolved for this visitor (after
  // worker pipeline: country → province → city → wishlist_geo_config). Only
  // by tracking the render can we compute "% of visitors in Mendoza saw
  // community CTA but didn't click" or "Tucumán visitors get hideCta=true,
  // is it intentional?". Fires once per mount.
  const ctaRenderTrackedRef = useRef(false);
  useEffect(() => {
    if (ctaRenderTrackedRef.current || typeof window === "undefined") return;
    ctaRenderTrackedRef.current = true;
    const resolvedVariant = hideCta
      ? "hidden"
      : showCommunity
        ? "community"
        : showBirthday
          ? "birthday"
          : showDualVideo
            ? "dual_video"
            : showWishlist
              ? "wishlist"
              : "none";
    const ph = (window as unknown as {
      posthog?: { get_distinct_id?: () => string };
    }).posthog;
    mirrorFunnelEvent({
      event_name: "base_template_cta_render",
      posthog_distinct_id: ph?.get_distinct_id?.(),
      session_id: getBaseTemplateSessionId(),
      template: "BaseTemplate",
      template_variant: resolvedVariant,
      flow_name: "hero",
      landing_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      country: countryCode,
      props: {
        variant: resolvedVariant,
        hideCta,
        showCommunity,
        showBirthday,
        showWishlist,
        province: templateConfig?.province ?? null,
        city: templateConfig?.city?.trim() || null,
        detection_method: templateConfig?.detectionMethod ?? null,
        worker_overrode: !heroCta,
      },
    });
  }, [hideCta, showCommunity, showBirthday, showDualVideo, showWishlist, heroCta, countryCode, templateConfig?.province, templateConfig?.city, templateConfig?.detectionMethod]);

  const internalCta = heroCta ?? (hideCta ? null : (
    showCommunity ? (
      <Link
        to="/invitacion-exclusive"
        onClick={() => {
          trackBaseTemplateClick({
            templateName: "BaseTemplate",
            elementId: "hero_community_cta",
            label: "Community CTA",
            surface: "hero",
            href: "/invitacion-exclusive",
            countryCode,
          });
        }}
        className="inline-flex items-center gap-3 py-3.5 px-5 rounded-[14px] max-w-[360px] mx-auto transition-all duration-200 bg-white/80 backdrop-blur-xl border border-white/70 shadow-lg shadow-black/[0.12] hover:bg-white/92 hover:border-white/90 hover:shadow-xl hover:shadow-black/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-black/10"
        aria-label={t("baseTemplate.community.ariaLabel")}
      >
        <Sparkles
          className="w-6 h-6 flex-shrink-0 text-purple-600"
          aria-hidden
        />
        <div className="flex flex-col gap-0.5 min-w-0 text-left">
          <span className="text-[15px] font-semibold text-black leading-tight">
            {t("baseTemplate.community.title")}
          </span>
          <span className="text-[13px] font-normal text-black/60 leading-tight">
            {t("baseTemplate.community.subtitle")}
          </span>
        </div>
      </Link>
    ) : showBirthday ? (
      (() => {
        const href = whatsappBroadcastHref;
        const isAppPath = href.startsWith("/") && !href.startsWith("//");
        const onNavigate = () =>
          trackBaseTemplateClick({
            templateName: "BaseTemplate",
            elementId: "hero_birthday_cta",
            label: "Comunidad CTA",
            surface: "hero",
            href,
            countryCode,
          });
        const className =
          "group inline-flex items-center gap-4 py-4 px-6 bg-[#25D366] hover:bg-[#20c05a] active:scale-[0.98] transition-all duration-200 max-w-[360px] md:max-w-[420px] mx-auto rounded-2xl shadow-xl shadow-green-500/20 focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2";
        const inner = (
          <>
            <span
              className="text-2xl flex-shrink-0 group-hover:scale-110 transition-transform"
              role="img"
              aria-label="WhatsApp"
            >
              💚
            </span>
            <div className="flex flex-col items-start text-left flex-1">
              <span className="text-base font-bold text-white leading-tight">
                {t("baseTemplate.birthday.title")}
              </span>
              <span className="text-xs text-white/80 leading-tight mt-1">
                {t("baseTemplate.birthday.subtitle")}
              </span>
            </div>
          </>
        );
        return isAppPath ? (
          <Link
            to={href}
            onClick={onNavigate}
            className={className}
            aria-label={t("baseTemplate.birthday.ariaLabel")}
          >
            {inner}
          </Link>
        ) : (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className={className}
            aria-label={t("baseTemplate.birthday.ariaLabel")}
          >
            {inner}
          </a>
        );
      })()
    ) : showDualVideo ? (
      // 🎬 Video Dual → /video-landing (TikTok Shop style, geo-activated via WishlistAdmin)
      <Link
        to="/video-landing"
        onClick={() =>
          trackBaseTemplateClick({
            templateName: "BaseTemplate",
            elementId: "hero_dual_video_cta",
            label: "Video Dual CTA",
            surface: "hero",
            href: "/video-landing",
            countryCode,
          })
        }
        className="group inline-flex items-center gap-4 py-4 px-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 active:scale-[0.98] transition-all duration-200 max-w-[360px] md:max-w-[420px] mx-auto rounded-2xl shadow-xl shadow-amber-500/20 focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
        aria-label={t("baseTemplate.dualVideo.ariaLabel")}
      >
        <Film
          className="w-6 h-6 flex-shrink-0 text-white group-hover:scale-110 transition-transform"
          aria-hidden
        />
        <div className="flex flex-col items-start text-left flex-1">
          <span className="text-base font-bold text-white leading-tight">
            {t("baseTemplate.dualVideo.title")}
          </span>
          <span className="text-xs text-white/80 leading-tight mt-1">
            {t("baseTemplate.dualVideo.subtitle")}
          </span>
        </div>
      </Link>
    ) : showWishlist ? (
      // 🎁 Wishlist (Internacional o AR via DB) + override admin menu_wishlist_url
      <a
        href={wishlistMenuUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackBaseTemplateClick({
            templateName: "BaseTemplate",
            elementId: "hero_wishlist_cta",
            label: "Wishlist CTA",
            surface: "hero",
            href: wishlistMenuUrl,
            countryCode,
          })
        }
        className="group inline-flex items-center gap-4 py-4 px-6 bg-gradient-to-r from-pink-500 to-rose-500 backdrop-blur-[16px] border border-pink-300/30 rounded-2xl hover:from-pink-600 hover:to-rose-600 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 max-w-[360px] md:max-w-[420px] mx-auto shadow-xl shadow-pink-500/20 focus-visible:ring-2 focus-visible:ring-pink-400 focus-visible:ring-offset-2"
        aria-label={t("baseTemplate.wishlist.ariaLabel")}
      >
        <span className="text-2xl flex-shrink-0 group-hover:scale-110 transition-transform" role="img" aria-label="🎁">🎁</span>
        <div className="flex flex-col items-start text-left flex-1">
          <span className="text-base font-bold text-white leading-tight">
            {t("baseTemplate.wishlist.title")}
          </span>
          <span className="text-xs text-white/80 leading-tight mt-1">
            {t("baseTemplate.wishlist.subtitle")}
          </span>
        </div>
      </a>
    ) : null
  ));

  const [selectedMedia, setSelectedMedia] = useState<
    (typeof mediaItems)[0] | null
  >(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Instagram browser optimizations
  const instagramOpts = getInstagramOptimizations();

  const lightboxOpenTimeRef = useRef<number | null>(null);
  const lightboxWasOpenRef = useRef(false);
  const lightboxViewedCountRef = useRef(1);

  // Mosaic layout state
  const [shuffledMedia, setShuffledMedia] = useState<typeof mediaItems>([]);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());

  // Allowed Mux playback IDs - fetched from Mux API based on metadata
  // Only used when skipMuxApiFilter = false (hardcoded mediaItems)
  const [allowedPlaybackIds, setAllowedPlaybackIds] = useState<string[]>([]);

  // Swipe gesture state for Instagram-like navigation (reel-style gallery)
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const SWIPE_THRESHOLD = 30; // Reduced threshold for more responsive swipes (reel-style)
  const SWIPE_VELOCITY_THRESHOLD = 0.2; // Reduced velocity threshold for smoother navigation

  // Tally form embed - init when script loads (handles script loading after component mount)
  useEffect(() => {
    const init = () => ensureTallyEmbeds();
    init();
    const t = setTimeout(init, 500);
    return () => clearTimeout(t);
  }, [tallyEmbedSrc]);

  // Block body scroll when lightbox is open (especially important for Instagram browser)
  useEffect(() => {
    if (selectedMedia) {
      // Store original values
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;

      // Block scroll - especially important in Instagram browser
      if (instagramOpts.isInstagramBrowser) {
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.height = "100%";
      }
      document.body.style.overflow = "hidden";

      // Cleanup function to restore scroll
      return () => {
        document.body.style.overflow = originalOverflow;
        if (instagramOpts.isInstagramBrowser) {
          document.body.style.position = originalPosition;
          document.body.style.width = originalWidth;
          document.body.style.height = originalHeight;
        }
      };
    }
  }, [selectedMedia, instagramOpts.isInstagramBrowser]);

  useEffect(() => {
    if (!selectedMedia) {
      lightboxWasOpenRef.current = false;
      trackImageClose("close");
      return;
    }
    lightboxOpenTimeRef.current = Date.now();
    if (selectedMedia.type === "photo") {
      trackImageOpen(
        {
          publicId: selectedMedia.src,
          title: selectedMedia.title,
          photoId: selectedMedia.id,
        },
        { templateName: "BaseTemplate", countryCode },
      );
    } else {
      // Switching from a photo to a video ends the photo's dwell.
      trackImageClose("switch_to_video");
    }
    if (!lightboxWasOpenRef.current) {
      lightboxViewedCountRef.current = 1;
      trackLightboxOpenedPostHog(
        selectedMedia.id,
        "gallery_click",
        selectedMedia.type,
        "BaseTemplate",
        countryCode,
      );
      lightboxWasOpenRef.current = true;
    }
  }, [selectedMedia, countryCode]);

  const closeLightbox = useCallback(
    (closeKind: "x" | "tap") => {
      setSelectedMedia((current) => {
        if (!current) return null;
        const openedAt = lightboxOpenTimeRef.current;
        const timeSpent = openedAt
          ? Math.max(0, Math.round((Date.now() - openedAt) / 1000))
          : 0;
        trackLightboxClosedPostHog(
          current.id,
          timeSpent,
          lightboxViewedCountRef.current,
          "BaseTemplate",
          countryCode,
        );
        trackBaseTemplateClick({
          templateName: "BaseTemplate",
          elementId:
            closeKind === "x" ? "lightbox_close_x" : "lightbox_close_tap",
          label:
            closeKind === "x"
              ? "Lightbox close (X)"
              : "Lightbox close (tap)",
          surface: "lightbox",
          countryCode,
          extra: {
            close_kind: closeKind,
            media_id: String(current.id),
          },
        });
        lightboxOpenTimeRef.current = null;
        return null;
      });
    },
    [countryCode],
  );

  // Navigation helpers for lightbox
  const navigateToPrevious = () => {
    if (!selectedMedia || shuffledMedia.length === 0) return;
    const currentIndex = shuffledMedia.findIndex(
      (item) => item.id === selectedMedia.id,
    );
    const prevIndex =
      currentIndex > 0 ? currentIndex - 1 : shuffledMedia.length - 1;
    const prevItem = shuffledMedia[prevIndex];
    trackLightboxNavigationPostHog(
      "prev",
      selectedMedia.id,
      prevItem.id,
      "BaseTemplate",
      countryCode,
    );
    lightboxViewedCountRef.current += 1;
    setSelectedMedia(prevItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  const navigateToNext = () => {
    if (!selectedMedia || shuffledMedia.length === 0) return;
    const currentIndex = shuffledMedia.findIndex(
      (item) => item.id === selectedMedia.id,
    );
    const nextIndex =
      currentIndex < shuffledMedia.length - 1 ? currentIndex + 1 : 0;
    const nextItem = shuffledMedia[nextIndex];
    trackLightboxNavigationPostHog(
      "next",
      selectedMedia.id,
      nextItem.id,
      "BaseTemplate",
      countryCode,
    );
    lightboxViewedCountRef.current += 1;
    setSelectedMedia(nextItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  // Fetch allowed Mux playback IDs from API based on metadata (External ID: "feed", Creator ID: brand)
  // Only runs when BaseTemplate is mounted (e.g. on /), not on /sorpresa or /exclusive
  // SKIP if using pre-validated media from database (skipMuxApiFilter = true)
  // 
  // CACHING STRATEGY (multi-layer to avoid repeated Mux API calls):
  // 1. localStorage cache (1 hour TTL) - survives page reloads for same user
  // 2. Memory cache (5 min TTL) - fast access within same session
  // 3. Server cache (1 hour TTL) - shared across users in same serverless instance
  // 4. HTTP cache (1 hour) - browser-level caching with stale-while-revalidate
  // 
  // Result: Same user reloading page = instant load from localStorage (no API call to Mux)
  // To invalidate cache when Mux content updates: use clearMuxCache() from useMuxCache hook
  useEffect(() => {
    // Skip Mux API filter if using pre-validated media from database
    if (skipMuxApiFilter) {
      if (import.meta.env.DEV) {
        console.log(
          "[BaseTemplate] Medios ya validados (BD); se omite filtro API Mux",
        );
      }
      return;
    }

    const fetchAllowedPlaybackIds = async () => {
      const playbackIds = await getBaseTemplateMuxPlaybackIds();

      if (playbackIds.length > 0) {
        setAllowedPlaybackIds(playbackIds);
      } else {
        setAllowedPlaybackIds(getFallbackPlaybackIds());
      }
    };

    fetchAllowedPlaybackIds();
  }, [skipMuxApiFilter]);

  // Initialize shuffled media for uniform grid
  // Two modes:
  // 1. skipMuxApiFilter = false (default): Filter videos using Mux API allowedPlaybackIds
  // 2. skipMuxApiFilter = true: Use all mediaItems as-is (pre-validated from database)
  // Resuelve public_id a URL para imágenes
  const resolveMediaSrc = (src: string | undefined) =>
    resolveLogoUrl(src) ?? src ?? "";

  // Excluir logo de la galería si está configurado (no debe mostrarse en header y gallery)
  const mediaWithoutLogo = useMemo(() => {
    if (!logoUrl?.trim()) return mediaItems;
    return mediaItems.filter(
      (item) => !isSameCloudinaryImage(logoUrl, resolveMediaSrc(item.src))
    );
  }, [mediaItems, logoUrl]);

  useEffect(() => {
    if (mediaWithoutLogo.length === 0) {
      return;
    }

    // Mode 1: Using pre-validated media from site settings (skip filter)
    if (skipMuxApiFilter) {
      // Preservar orden configurado en admin (site settings)
      setShuffledMedia(mediaWithoutLogo);
      return;
    }

    // Mode 2: Using hardcoded mediaItems (apply Mux API filter)
    if (allowedPlaybackIds.length > 0) {
      // Filter: Only show Mux videos with External ID "feed" and matching creator ID
      // BaseTemplate: Only shows videos with playbackIds from Mux API (filtered by metadata)
      // Photos from any source (including Cloudflare Images) are allowed
      const filteredMedia = mediaWithoutLogo.filter((item) => {
        // For videos: ONLY show videos with playbackIds from Mux API (metadata filtered)
        if (item.type === "video") {
          // Must have Mux playbackId
          if (!item.playbackId) {
            return false;
          }
          // Only allow videos with playbackIds from Mux API (External ID: "feed", creator ID from brand)
          if (!allowedPlaybackIds.includes(item.playbackId)) {
            return false;
          }
          // Exclude if src contains Cloudflare Stream URLs (extra safety)
          if (item.src && item.src.includes("cloudflarestream.com")) {
            return false;
          }
          return true;
        }

        // For photos: ALLOW ALL images - no filtering, show everything
        // Images should be visible regardless of source
        if (item.type === "photo") {
          return true;
        }

        return true;
      });

      const shuffled = [...filteredMedia].sort(() => Math.random() - 0.5);
      setShuffledMedia(shuffled);
      if (import.meta.env.DEV) {
        console.log(
          "[BaseTemplate] Medios filtrados (API Mux):",
          filteredMedia.length,
          "elementos",
        );
      }
    }
  }, [mediaWithoutLogo, allowedPlaybackIds, skipMuxApiFilter]);

  // Uniform gallery grid - formal, consistent layout for all items
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Hero CTA fade-in — evita pop-in visual del botón */
      @keyframes ctaFadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      /* Mobile: 2 columns */
      .gallery-grid-uniform {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 4px;
        width: 100%;
        max-width: 900px;
        margin: 0 auto;
        padding: 0 8px;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
      }
      
      .gallery-grid-uniform .gallery-grid-item {
        position: relative;
        overflow: hidden;
        background: #f5f5f5;
        border-radius: 4px;
        aspect-ratio: 1;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        border: 1px solid rgba(0,0,0,0.05);
        contain: layout paint;
      }
      
      .gallery-grid-uniform .gallery-grid-item img,
      .gallery-grid-uniform .gallery-grid-item video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        transition: transform 0.25s ease-out;
        image-rendering: -webkit-optimize-contrast;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        transform: translateZ(0);
      }
      
      .gallery-grid-uniform .gallery-grid-item:active img,
      .gallery-grid-uniform .gallery-grid-item:active video {
        transform: scale(0.98);
      }
      
      /* Desktop: 3 columns, max 9 fotos */
      @media (min-width: 640px) {
        .gallery-grid-uniform {
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          padding: 0 16px;
          max-width: 900px;
        }
        
        .gallery-grid-uniform .gallery-grid-item {
          border-radius: 6px;
        }
      }
      
      @media (min-width: 1024px) {
        .gallery-grid-uniform {
          gap: 10px;
          padding: 0 24px;
          max-width: 900px;
        }
        
        .gallery-grid-uniform .gallery-grid-item {
          border-radius: 8px;
        }
      }
      
      @media (hover: hover) and (pointer: fine) {
        .gallery-grid-uniform .gallery-grid-item:hover img,
        .gallery-grid-uniform .gallery-grid-item:hover video {
          transform: translateZ(0) scale(1.05);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Analytics + Instagram browser optimizations
  useEffect(() => {
    trackTemplateUsageGA("BaseTemplate", "LATAM", [
      "uniform_gallery",
      "mobile_first_responsive",
      "shuffle_functionality",
      ...(instagramOpts.isInstagramBrowser
        ? ["instagram_browser_optimized"]
        : []),
    ]);
    trackTemplateUsagePostHog("BaseTemplate", "LATAM", [
      "uniform_gallery",
      "mobile_first_responsive",
      "shuffle_functionality",
      ...(instagramOpts.isInstagramBrowser
        ? ["instagram_browser_optimized"]
        : []),
    ]);
    applyInstagramBrowserCSS();
  }, [instagramOpts.isInstagramBrowser]);

  return (
    <div
      className={`min-h-screen bg-white text-black ${instagramOpts.cssClasses.container}`}
    >
      {/* Header — barra carbón casi opaca (evita la “franja gris” del vidrio muy translúcido sobre fondo claro); blur suave solo como acabado */}
      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-neutral-950/95 supports-[backdrop-filter]:bg-neutral-950/88 backdrop-blur-xl pt-[env(safe-area-inset-top,0px)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_8px_28px_-12px_rgba(0,0,0,0.45)] [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-16 min-h-[44px]">
            {/* Logo - image from Cloudinary/Cloudflare or text fallback */}
            <div className="flex-shrink-0">
              <a
                href={normalizedHeaderUrl}
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "header_logo",
                    label: "Header logo",
                    surface: "header",
                    href: normalizedHeaderUrl,
                    countryCode,
                  })
                }
                className="inline-flex items-center gap-2 text-lg sm:text-xl font-display font-medium text-white whitespace-nowrap tracking-wider hover:text-white/90 transition-colors drop-shadow-sm"
                aria-label={brandName}
              >
                {logoUrl ? (
                  <img
                    src={resolveLogoUrl(logoUrl) ?? logoUrl}
                    alt={brandName}
                    className="h-8 sm:h-10 w-auto max-h-10 object-contain object-center"
                    loading="eager"
                  />
                ) : (
                  brandName
                )}
              </a>
            </div>

            {/* Nav + idioma + menú móvil (mismo patrón que SiteLayout) */}
            <div className="flex items-center justify-end gap-2 flex-shrink-0">
            <nav
              className="hidden md:flex space-x-8 mr-2"
              aria-label="Main navigation"
            >
              <a
                href="#home"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "nav_desktop_home",
                    label: t("baseTemplate.nav.home"),
                    surface: "nav_desktop",
                    href: "#home",
                    countryCode,
                  })
                }
                className="text-white font-medium transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-white/30 rounded tracking-wide text-sm"
              >
                {t("baseTemplate.nav.home")}
              </a>
              {showPortfolioNavLink && (
                <Link
                  to="/portfolio"
                  onClick={() =>
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_desktop_portfolio",
                      label: t("baseTemplate.nav.portfolio"),
                      surface: "nav_desktop",
                      href: "/portfolio",
                      countryCode,
                    })
                  }
                  className="text-white/80 hover:text-white transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-white/30 rounded tracking-wide text-sm"
                >
                  {t("baseTemplate.nav.portfolio")}
                </Link>
              )}
              <a
                href="#portfolio"
                onClick={(e) => {
                  e.preventDefault();
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "nav_desktop_gallery",
                    label: t("baseTemplate.nav.gallery"),
                    surface: "nav_desktop",
                    href: "#portfolio",
                    countryCode,
                  });
                  document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-white/80 hover:text-white transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-white/30 rounded tracking-wide text-sm"
              >
                {t("baseTemplate.nav.gallery")}
              </a>
              <a
                href="#contacto"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "nav_desktop_contacto",
                    label: t("baseTemplate.nav.contact"),
                    surface: "nav_desktop",
                    href: "#contacto",
                    countryCode,
                  })
                }
                className="text-white/80 hover:text-white transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-white/30 rounded tracking-wide text-sm"
              >
                {t("baseTemplate.nav.contact")}
              </a>
            </nav>
            <LanguageSwitcher variant="dark" />
            <div className="md:hidden">
              <button
                type="button"
                aria-label="Toggle navigation menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => {
                  const newState = !mobileMenuOpen;
                  setMobileMenuOpen(newState);
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "mobile_menu_toggle",
                    label: newState ? "Open mobile menu" : "Close mobile menu",
                    surface: "mobile_menu",
                    countryCode,
                    extra: { menu_open: newState },
                  });
                }}
                className="text-white/85 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30 rounded"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-neutral-950 supports-[backdrop-filter]:bg-neutral-950/98 backdrop-blur-lg shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a
                href="#home"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "nav_mobile_home",
                    label: t("baseTemplate.nav.home"),
                    surface: "nav_mobile",
                    href: "#home",
                    countryCode,
                  })
                }
                className="block px-3 py-2 text-white font-medium tracking-wide"
              >
                {t("baseTemplate.nav.home")}
              </a>
              {showPortfolioNavLink && (
                <Link
                  to="/portfolio"
                  onClick={() => {
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_mobile_portfolio",
                      label: t("baseTemplate.nav.portfolio"),
                      surface: "nav_mobile",
                      href: "/portfolio",
                      countryCode,
                    });
                    setMobileMenuOpen(false);
                  }}
                  className="block px-3 py-2 text-white/80 hover:text-white transition-colors font-medium tracking-wide"
                >
                  {t("baseTemplate.nav.portfolio")}
                </Link>
              )}
              <a
                href="#portfolio"
                onClick={(e) => {
                  e.preventDefault();
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "nav_mobile_gallery",
                    label: t("baseTemplate.nav.gallery"),
                    surface: "nav_mobile",
                    href: "#portfolio",
                    countryCode,
                  });
                  document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" });
                  setMobileMenuOpen(false);
                }}
                className="block px-3 py-2 text-white/80 hover:text-white transition-colors font-medium tracking-wide"
              >
                {t("baseTemplate.nav.gallery")}
              </a>
              <a
                href="#contacto"
                onClick={() => {
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "nav_mobile_contacto",
                    label: t("baseTemplate.nav.contact"),
                    surface: "nav_mobile",
                    href: "#contacto",
                    countryCode,
                  });
                  setMobileMenuOpen(false);
                }}
                className="block px-3 py-2 text-white/80 hover:text-white transition-colors font-medium tracking-wide"
              >
                {t("baseTemplate.nav.contact")}
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main id="main-content" className="relative">
        {/* Light Hero Section with Cloudflare Banner - Mobile first */}
        <section
          id="home"
          data-section-name="Hero"
          className="relative w-full bg-white overflow-hidden min-h-[60vh] sm:min-h-[65vh] lg:min-h-[70vh] group"
        >
          {/* Banner Image: site settings (primera de galería) o Cloudflare por defecto */}
          {/* bannerDeferred: evita flash imagen incorrecta mientras cargan site settings */}
          <div className="absolute inset-0">
            {!bannerDeferred && bannerImageUrl && (
            <img
              src={resolveLogoUrl(bannerImageUrl) ?? bannerImageUrl}
              alt={t("baseTemplate.galleryAlt.photoSuffix", { brandPersonName: brandName })}
              className="banner-image w-full h-full object-cover transition-all duration-700 ease-out group-hover:brightness-75"
              style={{ objectPosition: "center center", filter: "brightness(0.88)" }}
              loading="eager"
              onLoad={() => {
                // Image load tracking removed
                // Ensure the fallback background is hidden when image loads
                const fallbackBg = document.querySelector(
                  ".banner-fallback-bg",
                );
                if (fallbackBg) {
                  (fallbackBg as HTMLElement).style.opacity = "0";
                }
                // Ensure image is visible
                const img = document.querySelector(
                  ".banner-image",
                ) as HTMLElement;
                if (img) {
                  img.style.opacity = "1";
                }
              }}
              onError={(e) => {
                const target = e.target as HTMLElement;
                target.style.display = "none";

                // Show elegant fallback background
                const fallbackBg = document.querySelector(
                  ".banner-fallback-bg",
                );
                if (fallbackBg) {
                  (fallbackBg as HTMLElement).style.opacity = "1";
                }

                // Track the failure for analytics
                if (typeof window !== "undefined" && window.gtag) {
                  window.gtag("event", "image_load_error", {
                    event_category: "Cloudflare Images",
                    event_label: "banner_image_failed",
                    image_id: "9fdc1148-5ac5-48d3-2a3a-6ce149481300",
                    template: "BaseTemplate",
                    has_env_var: !!import.meta.env.VITE_CLOUDFLARE_ACCOUNT_HASH,
                  });
                }
              }}
              onLoadStart={() => {
                // Show loading state
                const img = document.querySelector(
                  ".banner-image",
                ) as HTMLElement;
                if (img) {
                  img.style.opacity = "0.5";
                }
              }}
            />
            )}
          </div>

          {/* Elegant Fallback Background - visible cuando bannerDeferred, sin banner configurado, o cuando falla la imagen */}
          <div
            className={`banner-fallback-bg absolute inset-0 transition-opacity duration-500 ${bannerDeferred || !bannerImageUrl ? "opacity-100" : "opacity-0"}`}
            style={{
              background: `
              linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%),
              radial-gradient(circle at 20% 50%, #ffecd2 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #fcb69f 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, #a8edea 0%, transparent 50%)
            `,
              backgroundBlendMode: "overlay, normal, normal, normal",
            }}
          >
            {/* Subtle pattern overlay for texture */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3Ccircle cx='53' cy='7' r='1'/%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3Ccircle cx='7' cy='53' r='1'/%3E%3Ccircle cx='53' cy='53' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: "60px 60px",
              }}
            />
          </div>

          {/* Subtle Grain Texture */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='4' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundSize: "200px 200px",
            }}
          ></div>

          {/* Subtle dark overlay on hover for luxury feel */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-700 ease-out pointer-events-none" />

          {/* Hero Content - Mobile first: texto un poco más arriba, sin tapar la cara */}
          <div className="relative min-h-[60vh] sm:min-h-[65vh] lg:min-h-[70vh] flex items-end justify-center px-4 pb-14 pt-16 sm:px-6 sm:pb-20 sm:pt-20 lg:px-8 lg:pb-24 lg:pt-24">
            <div className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8 lg:space-y-10">
              <div className="space-y-4 sm:space-y-6">
                {heroTitle?.trim() && (() => {
                  const raw = heroTitle.trim();
                  // Split por | → H1 solo nombre, subtítulo en <p> (SEO: H1 limpio)
                  const parts = raw.split(/\s*[\u007C\uFF5C\u00A6]\s*/);
                  const name = parts[0]?.trim() || raw;
                  const subtitle = parts.length >= 2 ? parts.slice(1).join(" ").trim() : "";
                  return (
                    <>
                      <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white tracking-tight leading-tight [text-shadow:0_2px_24px_rgba(0,0,0,0.55),0_1px_4px_rgba(0,0,0,0.4)]">
                        {name}
                      </h1>
                      {subtitle && (
                        <p className="font-body text-base sm:text-xl md:text-2xl lg:text-3xl text-white font-medium [text-shadow:0_2px_16px_rgba(0,0,0,0.5),0_1px_3px_rgba(0,0,0,0.35)]">
                          {subtitle}
                        </p>
                      )}
                    </>
                  );
                })()}
                {heroDescription?.trim() && (
                  <p className="font-body text-sm sm:text-lg md:text-xl lg:text-2xl text-white/95 max-w-2xl sm:max-w-3xl mx-auto leading-relaxed font-light px-2 sm:px-0 [text-shadow:0_2px_14px_rgba(0,0,0,0.45),0_1px_2px_rgba(0,0,0,0.35)]">
                    {heroDescription.trim()}
                  </p>
                )}
              </div>

              {/* WhatsApp broadcast CTA - only when worker injects whatsappBroadcast (e.g. AR provinces) */}
              {showWhatsAppBroadcast && (
                <a
                  href={whatsappBroadcastHref}
                  onClick={() => {
                    trackWhatsAppBroadcastClickGA();
                    trackWhatsAppBroadcastMeta();
                    trackWhatsAppBroadcastPostHog();
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "hero_whatsapp_broadcast",
                      label: "WhatsApp broadcast CTA",
                      surface: "hero",
                      href: whatsappBroadcastHref,
                      countryCode,
                    });
                  }}
                  className="inline-flex items-center gap-3 py-3.5 px-5 bg-black/[0.08] backdrop-blur-[16px] border border-black/[0.15] rounded-[14px] hover:bg-black/[0.12] hover:border-black/[0.25] transition-all duration-200 max-w-[360px] mx-auto"
                >
                  <svg
                    className="w-6 h-6 flex-shrink-0 text-[#25D366]"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <div className="flex flex-col gap-0.5 min-w-0 text-left">
                    <span className="text-[15px] font-semibold text-black leading-tight">
                      {t("baseTemplate.whatsappBroadcast.title")}
                    </span>
                    <span className="text-[13px] font-normal text-black/60 leading-tight">
                      {t("baseTemplate.whatsappBroadcast.subtitle")}
                    </span>
                  </div>
                </a>
              )}

              {/* Botón CTA — cumpleaños en AR; wishlist solo si heroCta / International */}
              {internalCta && (
                <div
                  className="animate-fade-in"
                  style={{ animation: "ctaFadeIn 0.4s ease-out both" }}
                >
                  {internalCta}
                </div>
              )}

              {/* Social Links - Removed (Instagram and YouTube) */}
            </div>
          </div>
        </section>

        {/* Portfolio/Gallery Section - Light style */}
        <section
          id="portfolio"
          data-section-name="Gallery"
          className="py-6 px-2 sm:py-12 sm:px-4 md:py-16 md:px-6 lg:py-20 lg:px-8 bg-gray-50"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-4 sm:mb-8 md:mb-12">
              <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-black mb-2 sm:mb-4 tracking-wide">
                {t("baseTemplate.gallery.title")}
              </h2>
              {/* Gallery automatically shuffles on load - no manual button needed */}
            </div>

            {/* Gallery: uniform grid - formal, consistent layout. No mostrar hasta tener site settings. */}
            {galleryDeferred ? (
              <div className="min-h-[200px] sm:min-h-[280px] flex items-center justify-center text-black/30 text-sm">
                {t("baseTemplate.gallery.loading")}
              </div>
            ) : (() => {
              const displayMedia = shuffledMedia
                .filter((item) => !failedImages.has(item.id))
                .slice(0, 9);
              return (
            <div className={`gallery-grid-uniform mb-6 sm:mb-12 ${instagramOpts.cssClasses.gallery}`}>
              {displayMedia.map((item, index) => (
                  <div
                    key={item.id}
                    className={`gallery-grid-item relative bg-gray-100 overflow-hidden touch-manipulation cursor-pointer ${index === 8 ? "hidden sm:block" : ""}`}
                  >
                    {/* Simple click to open - No hover arrows, super user friendly */}
                    <button
                      type="button"
                      onClick={() => {
                        trackMediaItemClickPostHog(
                          item.id,
                          item.type,
                          item.title,
                          index,
                          "BaseTemplate",
                          countryCode,
                        );
                        setSelectedMedia(item);
                      }}
                      aria-label={t("baseTemplate.galleryAlt.viewAria", {
                        type:
                          item.title ||
                          t("baseTemplate.galleryAlt.contentFallback"),
                      })}
                      className="w-full h-full focus:outline-none focus:ring-2 focus:ring-foreground/20 active:opacity-90"
                    >
                      <img
                        src={
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? getCloudinaryUrl(resolveMediaSrc(item.src), { watermark: true })
                            : item.src
                        }
                        srcSet={
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? `${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 400, watermark: true })} 400w, ${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 800, watermark: true })} 800w, ${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 1200, watermark: true })} 1200w`
                            : item.src?.includes("image.mux.com")
                              ? `${item.src}?width=400 400w, ${item.src}?width=800 800w, ${item.src}?width=1200 1200w`
                              : undefined
                        }
                        sizes={
                          item.src?.includes("image.mux.com") ||
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? "(max-width: 639px) 50vw, (max-width: 1023px) 33vw, (max-width: 1535px) 25vw, 20vw"
                            : undefined
                        }
                        alt={`${item.title?.trim() ? `${item.title.trim()} — ` : ""}${
                          item.type === "video"
                            ? t("baseTemplate.galleryAlt.videoSuffix")
                            : t("baseTemplate.galleryAlt.photoSuffix")
                        }`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          // Hide broken images silently - don't show in gallery
                          setFailedImages((prev) => new Set(prev).add(item.id));
                          const target = e.target as HTMLImageElement;
                          if (target) {
                            target.style.display = "none";
                          }
                        }}
                      />
                      {/* Play button overlay for videos - Always visible, simple */}
                      {item.type === "video" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/10">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                            <Play
                              className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-black ml-0.5"
                              fill="currentColor"
                            />
                          </div>
                        </div>
                      )}
                      {/* Title overlay - Removed for cleaner reel-style experience */}
                    </button>
                  </div>
                ))}
            </div>
              );
            })()}

            {/* CTA buttons removed - BaseTemplate should only have gallery and social links */}
          </div>
        </section>

        {/* Contact Section - Tally form embed */}
        <section
          id="contacto"
          data-section-name="Contact"
          className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 md:px-8 bg-white"
        >
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-2xl md:text-3xl font-display font-bold text-black mb-2 tracking-wide">
                {t("baseTemplate.contact.title")}
              </h2>
              <p className="text-sm sm:text-base text-black/70">
                {t("baseTemplate.contact.subtitle")}
              </p>
            </div>
            <div className="w-full min-h-[420px] sm:min-h-[500px] rounded-lg overflow-hidden bg-gray-50/50 border border-black/5">
              <iframe
                key={tallyEmbedSrc}
                data-tally-src={tallyEmbedSrc}
                loading="lazy"
                width="100%"
                height="578"
                frameBorder="0"
                marginHeight={0}
                marginWidth={0}
                title={t("baseTemplate.contact.iframeTitle")}
                allow="clipboard-write"
                className="w-full min-h-[420px] sm:min-h-[500px] border-0"
              />
            </div>
          </div>
        </section>
      </main>

      <BaseTemplateFooter
        variant="light"
        language={language}
        contactHref="#contacto"
        analyticsTemplate="BaseTemplate"
        countryCode={countryCode}
        brandName={brandName}
        footerTagline={footerTagline}
        showPortfolioNavLink={showPortfolioNavLink}
      />

      {/* Reel-style Lightbox Modal - Instagram-like vertical swipe experience */}
      {selectedMedia && (
        <div
          className={`fixed inset-0 z-50 bg-white ${
            instagramOpts.cssClasses.modal || ""
          }`}
          style={{
            touchAction: "none",
          }}
        >
          {/* Close Button - Safe area aware */}
          <button
            type="button"
            onClick={() => closeLightbox("x")}
            className="absolute w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-black/90 hover:text-black bg-white/60 rounded-full hover:bg-white/80 transition-all duration-200 z-30 focus:outline-none focus:ring-2 focus:ring-black/50 touch-manipulation active:scale-95"
            style={{
              top: "max(1rem, env(safe-area-inset-top, 0px))",
              right: "max(1rem, env(safe-area-inset-right, 0px))",
            }}
            aria-label={t("baseTemplate.lightbox.close")}
          >
            <X className="w-6 h-6" strokeWidth={2.5} />
          </button>

          {/* Media counter indicator */}
          <div
            className="absolute left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 bg-white/60 rounded-full text-black/80 text-xs font-medium tracking-wide"
            style={{
              top: "max(1rem, env(safe-area-inset-top, 0px))",
            }}
          >
            {shuffledMedia.findIndex((item) => item.id === selectedMedia.id) +
              1}{" "}
            /{" "}
            {shuffledMedia.filter((item) => !failedImages.has(item.id)).length}
          </div>

          {/* Navigation buttons - visibles en mobile y desktop */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              trackBaseTemplateClick({
                templateName: "BaseTemplate",
                elementId: "lightbox_nav_prev",
                label: "Lightbox previous",
                surface: "lightbox",
                countryCode,
              });
              navigateToPrevious();
            }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-black/60 hover:text-black/90 bg-white/50 rounded-full hover:bg-white/70 transition-all duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-black/30 touch-manipulation active:scale-95"
            aria-label={t("baseTemplate.lightbox.previous")}
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              trackBaseTemplateClick({
                templateName: "BaseTemplate",
                elementId: "lightbox_nav_next",
                label: "Lightbox next",
                surface: "lightbox",
                countryCode,
              });
              navigateToNext();
            }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-black/60 hover:text-black/90 bg-white/50 rounded-full hover:bg-white/70 transition-all duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-black/30 touch-manipulation active:scale-95"
            aria-label={t("baseTemplate.lightbox.next")}
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2} />
          </button>

          {/* Reel Content - Full screen swipeable */}
          <div
            className="relative w-full h-full flex items-center justify-center overflow-hidden"
            style={{
              height: "100dvh",
              WebkitOverflowScrolling: "touch",
            }}
            onTouchStart={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest("mux-player") ||
                target.closest(".action-button-container")
              ) {
                return;
              }
              const touch = e.touches[0];
              swipeStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now(),
              };
            }}
            onTouchMove={(e) => {
              if (swipeStartRef.current) {
                const touch = e.touches[0];
                const deltaX = Math.abs(
                  touch.clientX - swipeStartRef.current.x,
                );
                const deltaY = Math.abs(
                  touch.clientY - swipeStartRef.current.y,
                );
                // Prevent default to allow smooth swipe animation
                if (deltaX > 10 || deltaY > 10) {
                  e.preventDefault();
                }
              }
            }}
            onTouchEnd={(e) => {
              if (!swipeStartRef.current) return;

              const target = e.target as HTMLElement;
              if (
                target.closest("mux-player") ||
                target.closest(".action-button-container")
              ) {
                swipeStartRef.current = null;
                return;
              }

              const touch = e.changedTouches[0];
              const deltaX = touch.clientX - swipeStartRef.current.x;
              const deltaY = touch.clientY - swipeStartRef.current.y;
              const deltaTime = Date.now() - swipeStartRef.current.time;
              const velocityX = Math.abs(deltaX) / deltaTime;
              const velocityY = Math.abs(deltaY) / deltaTime;

              const swipeThreshold = instagramOpts.isInstagramBrowser ? 20 : 30;
              const velocityThreshold = 0.15;

              // Vertical swipe - Instagram reel style (up = next, down = previous)
              if (
                Math.abs(deltaY) > Math.abs(deltaX) &&
                Math.abs(deltaY) > swipeThreshold &&
                velocityY > velocityThreshold
              ) {
                if (deltaY < 0) {
                  // Swipe up - next item
                  navigateToNext();
                } else {
                  // Swipe down - previous item
                  navigateToPrevious();
                }
              }
              // Horizontal swipe - alternative navigation
              else if (
                Math.abs(deltaX) > Math.abs(deltaY) &&
                Math.abs(deltaX) > swipeThreshold &&
                velocityX > velocityThreshold
              ) {
                if (deltaX > 0) {
                  navigateToPrevious();
                } else {
                  navigateToNext();
                }
              }
              // Tap to close (if minimal movement)
              else if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
                closeLightbox("tap");
              }

              swipeStartRef.current = null;
            }}
          >
            {/* Animated content wrapper */}
            <div
              className="w-full h-full flex items-center justify-center transition-transform duration-300 ease-out"
              key={selectedMedia.id}
              style={{
                animation: "fadeSlideIn 0.25s ease-out",
              }}
            >
              {selectedMedia.type === "video" && selectedMedia.playbackId ? (
                <div className="relative w-full h-full flex items-center justify-center bg-white">
                  <MuxVideoPlayer
                    key={selectedMedia.id}
                    playbackId={selectedMedia.playbackId}
                    isVisible={true}
                    displayMode="reel"
                    autoplay={true}
                    // Instagram in-app browser blocks autoplay with audio.
                    // Start muted there to guarantee playback starts for all users.
                    muted={instagramOpts.isInstagramBrowser}
                    loop={true}
                    controls={false}
                    actionButton={selectedMedia.actionButton}
                  />
                </div>
              ) : (
                <div className="relative w-full h-full flex items-center justify-center bg-white px-2">
                  <img
                    key={selectedMedia.id}
                    src={
                      resolveMediaSrc(selectedMedia.src).includes("res.cloudinary.com")
                        ? getCloudinaryUrl(resolveMediaSrc(selectedMedia.src), {
                            width: 1600,
                            watermark: true,
                          })
                        : selectedMedia.src
                    }
                    alt={`${selectedMedia.title?.trim() ? `${selectedMedia.title.trim()} — ` : ""}${
                      selectedMedia.type === "video"
                        ? t("baseTemplate.galleryAlt.videoSuffix")
                        : t("baseTemplate.galleryAlt.photoSuffix")
                    }`}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                    style={{
                      maxHeight: "85vh",
                      animation: "scaleIn 0.3s ease-out",
                    }}
                  />
                </div>
              )}
            </div>

            {/* Hint mobile: desliza o flechas para cambiar, toca para cerrar */}
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-black/50 pointer-events-none md:hidden"
              style={{
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
              }}
            >
              <span className="text-xs tracking-wide">
                {t("baseTemplate.lightbox.swipeHint")}
              </span>
            </div>
          </div>

          {/* CSS Animation keyframes */}
          <style>{`
            @keyframes fadeSlideIn {
              from {
                opacity: 0.5;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes scaleIn {
              from {
                opacity: 0.7;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};
